import { z } from 'zod';
import manifest from './api-manifest.json';
export const apiErrorSchema = z.object({
    code: z.string().optional(),
    details: z.unknown().optional(),
    error: z.string()
}).passthrough();
export const userInfoSchema = z.object({
    authenticatedAt: z.number().optional(),
    corporateAccountId: z.string().optional(),
    email: z.string().optional(),
    expiresAt: z.number().optional(),
    id: z.string().optional(),
    isAuthenticated: z.boolean().optional(),
    name: z.string().optional(),
    organization: z.string().optional(),
    organizationId: z.string().optional(),
    phoneNumber: z.string().optional(),
    primaryPhone: z.string().optional(),
    role: z.string().optional(),
    serviceModes: z.array(z.string()).optional(),
    tenantId: z.string().optional()
}).passthrough();
export const authLoginResponseSchema = z.object({
    token: z.string(),
    user: userInfoSchema.optional()
}).passthrough();
export const authRefreshResponseSchema = z.object({
    token: z.string()
}).passthrough();
const nullableText = z.string().nullable().optional();
/**
 * One GET /api/contacts row as it is sent: a database row (snake_case, with
 * nulls for empty columns; vrs-server src/db/contacts.ts getContacts), saved
 * contacts first, then platform directory people (id 'platform-<account>',
 * organization/notes/avatar_color null). Older callers and fixtures used
 * camelCase, which is still accepted. Map a row with contactsScreenUtils
 * normalizeContact (native Contacts) or groupInvite toInviteContact (in-call
 * invite); never read one field spelling only.
 */
export const contactSchema = z.object({
    avatarColor: nullableText,
    avatar_color: nullableText,
    email: nullableText,
    groupIds: nullableText,
    groupNames: nullableText,
    group_ids: nullableText,
    group_names: nullableText,
    handle: nullableText,
    id: z.string(),
    isFavorite: z.boolean().nullable().optional(),
    is_favorite: z.boolean().nullable().optional(),
    lastCalled: nullableText,
    linkedClientId: nullableText,
    linked_client_id: nullableText,
    name: z.string(),
    notes: nullableText,
    // A saved contact with no linked account whose number an account holds:
    // that account (the in-call invite rings it; getContacts withNumberAccounts).
    numberClientId: nullableText,
    number_client_id: nullableText,
    organization: nullableText,
    phoneNumber: nullableText,
    phone_number: nullableText,
    // Their own photo, a signed link (people who have them as a contact).
    photoUrl: nullableText,
    photo_url: nullableText
}).passthrough();
export const contactsListResponseSchema = z.union([
    z.array(contactSchema),
    z.object({
        contacts: z.array(contactSchema)
    }).passthrough()
]);
export const contactHandleSchema = z.object({
    active: z.boolean().optional(),
    clientId: z.string().optional(),
    handle: z.string(),
    id: z.string(),
    isPrimary: z.boolean().optional(),
    normalizedHandle: z.string().optional(),
    tenantId: z.string().optional(),
    visibility: z.enum(['public', 'private']).optional()
}).passthrough();
export const contactRequestSchema = z.object({
    createdAt: z.string().optional(),
    id: z.string(),
    message: z.string().optional().nullable(),
    recipientClientId: z.string().optional(),
    recipientHandle: z.string().optional().nullable(),
    recipientName: z.string().optional(),
    recipientPhoneNumber: z.string().optional().nullable(),
    requesterClientId: z.string().optional(),
    requesterHandle: z.string().optional().nullable(),
    requesterName: z.string().optional(),
    requesterPhoneNumber: z.string().optional().nullable(),
    respondedAt: z.string().optional().nullable(),
    source: z.string().optional().nullable(),
    status: z.enum(['pending', 'accepted', 'rejected', 'cancelled', 'blocked']),
    tenantId: z.string().optional()
}).passthrough();
export const contactRequestsResponseSchema = z.object({
    requests: z.array(contactRequestSchema)
}).passthrough();
export const callRecordSchema = z.object({
    contactName: z.string(),
    direction: z.enum(['outgoing', 'incoming', 'missed']),
    duration: z.number(),
    id: z.string(),
    interpreterName: z.string().optional(),
    phoneNumber: z.string(),
    timestamp: z.string()
}).passthrough();
export const callHistoryResponseSchema = z.union([
    z.array(callRecordSchema),
    z.object({
        calls: z.array(callRecordSchema)
    }).passthrough()
]);
export const voicemailSchema = z.object({
    duration: z.number(),
    fromName: z.string(),
    fromPhone: z.string().optional(),
    id: z.string(),
    isRead: z.boolean(),
    playbackUrl: z.string().optional(),
    thumbnailUrl: z.string().optional(),
    timestamp: z.string(),
    transcript: z.string().optional()
}).passthrough();
export const voicemailInboxResponseSchema = z.union([
    z.array(voicemailSchema),
    z.object({
        messages: z.array(voicemailSchema).optional(),
        voicemails: z.array(voicemailSchema).optional()
    }).passthrough()
]);
export const voicemailUnreadCountResponseSchema = z.object({
    count: z.number()
}).passthrough();
export const notificationDeviceSchema = z.object({
    appId: z.string().nullable().optional(),
    createdAt: z.string().optional(),
    deviceId: z.string(),
    enabled: z.boolean(),
    failureCount: z.number().optional(),
    hasAlertToken: z.boolean().optional(),
    hasPushToken: z.boolean(),
    id: z.string(),
    lastErrorAt: z.string().nullable().optional(),
    lastRegisteredAt: z.string().optional(),
    lastSeenAt: z.string().optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
    platform: z.enum(['ios', 'android', 'web', 'desktop', 'native', 'unknown']),
    pushProvider: z.enum(['apns', 'fcm', 'web-push', 'local', 'unknown']),
    role: z.enum(['client', 'interpreter']),
    tenantId: z.string().optional(),
    tokenFingerprint: z.string().nullable().optional(),
    userId: z.string()
}).passthrough();
export const notificationDeviceRegistrationResponseSchema = z.object({
    device: notificationDeviceSchema
}).passthrough();
export const webPushPublicKeyResponseSchema = z.object({
    publicKey: z.string().nullable(),
    ready: z.boolean()
}).passthrough();
export const billingUsageSummarySchema = z.object({
    currency: z.string().optional(),
    day: z.unknown().optional(),
    month: z.unknown().optional(),
    totalMinutes: z.number().optional(),
    totalUsd: z.number().optional(),
    week: z.unknown().optional()
}).passthrough();
export const vriScheduledSessionSchema = z.object({
    revision: z.string().uuid().optional(),
    calendarEventId: z.string().nullable().optional(),
    calendarHtmlLink: z.string().nullable().optional(),
    calendarProvider: z.string().optional(),
    calendarSyncError: z.string().nullable().optional(),
    calendarSyncedAt: z.string().nullable().optional(),
    callId: z.string().nullable().optional(),
    cancelledAt: z.string().nullable().optional(),
    completedAt: z.string().nullable().optional(),
    corporateAccountId: z.string().nullable().optional(),
    corporateCcEmails: z.array(z.string()).optional(),
    corporateContactEmail: z.string().nullable().optional(),
    costCenter: z.string().nullable().optional(),
    createdAt: z.string().optional(),
    department: z.string().nullable().optional(),
    endsAt: z.string(),
    externalMeetingUrl: z.string().nullable().optional(),
    hostEmail: z.string().nullable().optional(),
    hostName: z.string().nullable().optional(),
    id: z.string(),
    industryTag: z.string().nullable().optional(),
    instructions: z.string().nullable().optional(),
    interpreterRequirements: z.array(z.string()).optional(),
    language: z.string(),
    meetingProvider: z.string().nullable().optional(),
    participantEmails: z.array(z.string()).optional(),
    profileId: z.string().nullable().optional(),
    queueRequestId: z.string().nullable().optional(),
    recurrenceRule: z.string().nullable().optional(),
    secondaryLanguage: z.string().nullable().optional(),
    startsAt: z.string(),
    status: z.string(),
    tenantId: z.string().nullable().optional(),
    timezone: z.string().optional(),
    title: z.string(),
    updatedAt: z.string().optional()
}).passthrough();
export const vriCalendarConnectionSchema = z.object({
    accountEmail: z.string().nullable().optional(),
    expiresAt: z.string().nullable().optional(),
    id: z.string(),
    lastSyncedAt: z.string().nullable().optional(),
    provider: z.string(),
    remindersEnabled: z.boolean().optional(),
    scope: z.string().nullable().optional(),
    status: z.string(),
    syncError: z.string().nullable().optional(),
    tenantId: z.string().nullable().optional(),
    updatedAt: z.string().optional()
}).passthrough();
export const vriCalendarConnectionsResponseSchema = z.object({
    connections: z.array(vriCalendarConnectionSchema)
}).passthrough();
// Calendar reminders ("Interpret my meeting", phase 2): never a meeting link.
export const meetingReminderSchema = z.object({
    endsAt: z.string(),
    host: z.string(),
    id: z.string(),
    platform: z.enum(['zoom', 'meet', 'teams', 'webex']),
    startsAt: z.string(),
    state: z.string().optional(),
    title: z.string().nullable().optional()
}).strict();
export const meetingRemindersResponseSchema = z.object({
    reminders: z.array(meetingReminderSchema)
}).passthrough();
export const calendarRemindersToggleResponseSchema = z.object({
    provider: z.string(),
    remindersEnabled: z.boolean()
}).passthrough();
export const vriScheduledSessionsResponseSchema = z.object({
    hasMore: z.boolean().optional(),
    limit: z.number().int().min(1).max(100).optional(),
    nextOffset: z.number().int().nonnegative().nullable().optional(),
    offset: z.number().int().nonnegative().optional(),
    sessions: z.array(vriScheduledSessionSchema)
}).passthrough();
export const vriSchedulingProfileSchema = z.object({
    active: z.boolean().optional(),
    corporateCcEmails: z.array(z.string()).optional(),
    corporateContactEmail: z.string().nullable().optional(),
    costCenter: z.string().nullable().optional(),
    department: z.string().nullable().optional(),
    id: z.string(),
    industryTag: z.string().nullable().optional(),
    instructions: z.string().nullable().optional(),
    interpreterRequirements: z.array(z.string()).optional(),
    isDefault: z.boolean().optional(),
    language: z.string().optional(),
    name: z.string(),
    participantEmails: z.array(z.string()).optional()
}).passthrough().transform(({ corporateAccountId: _retiredBillingAuthority, ...profile }) => profile);
export const vriSchedulingProfilesResponseSchema = z.object({
    profiles: z.array(vriSchedulingProfileSchema)
}).passthrough();
export const vriScheduledSessionResponseSchema = z.object({
    followUpWarnings: z.array(z.enum(['notifications', 'calendar', 'webhooks', 'queue'])).optional(),
    calendarSync: z.object({
        error: z.string().optional(),
        eventId: z.string().optional(),
        htmlLink: z.string().nullable().optional(),
        provider: z.string().optional(),
        skipped: z.boolean().optional()
    }).nullable().optional(),
    icsDownloadUrl: z.string().optional(),
    session: vriScheduledSessionSchema
}).passthrough();
export const vriSchedulingReportResponseSchema = z.object({
    report: z.object({
        byIndustry: z.record(z.string(), z.number()).optional(),
        cancelledCount: z.number(),
        completedCount: z.number(),
        inProgressCount: z.number(),
        scheduledCount: z.number(),
        scheduledMinutes: z.number(),
        upcomingCount: z.number()
    }).passthrough()
}).passthrough();
export const externalMeetingProviderSchema = z.object({
    clientIdConfigured: z.boolean(),
    clientSecretConfigured: z.boolean(),
    provider: z.enum(['zoom']),
    redirectUriConfigured: z.boolean(),
    scopes: z.string(),
    tokenEncryptionConfigured: z.boolean(),
    webhookSecretConfigured: z.boolean()
}).passthrough();
export const externalMeetingProvidersResponseSchema = z.object({
    providers: z.array(externalMeetingProviderSchema),
    ready: z.boolean()
}).passthrough();
export const zoomConnectionSchema = z.object({
    created_at: z.string().optional(),
    expires_at: z.string().nullable().optional(),
    has_access_token: z.boolean(),
    has_refresh_token: z.boolean(),
    id: z.string(),
    owner_role: z.string().optional(),
    owner_user_id: z.string().optional(),
    provider: z.enum(['zoom']),
    scope: z.string().nullable().optional(),
    status: z.string(),
    tenant_id: z.string().optional(),
    token_type: z.string().nullable().optional(),
    updated_at: z.string().optional()
}).passthrough();
export const zoomConnectionsResponseSchema = z.object({
    connections: z.array(zoomConnectionSchema)
}).passthrough();
export const zoomConnectUrlResponseSchema = z.object({
    authorizationUrl: z.string().url(),
    provider: z.enum(['zoom'])
}).passthrough();
export const waitingMediaResponseSchema = z.object({
    media: z.object({
        altText: z.string().nullable().optional(),
        captionText: z.string().nullable().optional(),
        enabled: z.boolean(),
        mediaType: z.string().optional(),
        mediaUrl: z.string().nullable().optional(),
        posterUrl: z.string().nullable().optional(),
        reducedMotionUrl: z.string().nullable().optional(),
        tenantId: z.string()
    }).passthrough()
}).passthrough();
export const externalMeetingRecordSchema = z.object({
    externalMeetingRecordId: z.string(),
    provider: z.enum(['zoom', 'teams', 'webex', 'other']),
    status: z.string(),
    tenantId: z.string()
}).passthrough();
export const externalMeetingRecordsResponseSchema = z.object({
    records: z.array(externalMeetingRecordSchema)
}).passthrough();
export const externalMeetingRecordResponseSchema = z.object({
    record: externalMeetingRecordSchema
}).passthrough();
export const externalMeetingEventsResponseSchema = z.object({
    events: z.array(z.object({
        eventType: z.string(),
        id: z.string(),
        provider: z.string()
    }).passthrough())
}).passthrough();
export const externalMeetingParticipantsResponseSchema = z.object({
    participants: z.array(z.object({
        id: z.string(),
        provider: z.string(),
        recordId: z.string()
    }).passthrough())
}).passthrough();
export const externalMeetingWebhookResponseSchema = z.object({
    event: z.object({
        eventType: z.string(),
        id: z.string(),
        provider: z.string()
    }).passthrough(),
    ok: z.boolean(),
    record: externalMeetingRecordSchema
}).passthrough();
/**
 * GET /api/rings/:ringId and POST /api/rings/:ringId/decline, asked by a
 * ringing phone's native code with its ring token (docs/push-ring-lifecycle.md).
 * 401 and 404 mean the ring is over for this phone.
 */
export const ringStatusResponseSchema = z.object({
    ringId: z.string(),
    state: z.enum(['ringing', 'ended']),
    reason: z.enum(['caller_cancelled', 'answered_elsewhere', 'declined_elsewhere', 'timeout', 'ended_for_all', 'removed',
        'call_ended']).optional(),
    deadline: z.number().optional()
}).passthrough();
export const ringDeclineResponseSchema = ringStatusResponseSchema.extend({
    // call: the call heard the decline; device: only this phone's ring
    // ("Interpreter ready", or a decline the call refused); none: a replay.
    scope: z.enum(['call', 'device', 'none']),
    code: z.string().optional()
}).passthrough();
export const apiContractSchemas = {
    authLoginResponse: authLoginResponseSchema,
    authRefreshResponse: authRefreshResponseSchema,
    billingUsageSummary: billingUsageSummarySchema,
    calendarRemindersToggleResponse: calendarRemindersToggleResponseSchema,
    callHistoryResponse: callHistoryResponseSchema,
    contactRequestsResponse: contactRequestsResponseSchema,
    contactsListResponse: contactsListResponseSchema,
    externalMeetingEventsResponse: externalMeetingEventsResponseSchema,
    externalMeetingParticipantsResponse: externalMeetingParticipantsResponseSchema,
    externalMeetingProvidersResponse: externalMeetingProvidersResponseSchema,
    externalMeetingRecordResponse: externalMeetingRecordResponseSchema,
    externalMeetingRecordsResponse: externalMeetingRecordsResponseSchema,
    externalMeetingWebhookResponse: externalMeetingWebhookResponseSchema,
    meetingRemindersResponse: meetingRemindersResponseSchema,
    notificationDeviceRegistrationResponse: notificationDeviceRegistrationResponseSchema,
    userInfo: userInfoSchema,
    vriCalendarConnectionsResponse: vriCalendarConnectionsResponseSchema,
    vriScheduledSessionResponse: vriScheduledSessionResponseSchema,
    vriScheduledSessionsResponse: vriScheduledSessionsResponseSchema,
    vriSchedulingProfilesResponse: vriSchedulingProfilesResponseSchema,
    vriSchedulingReportResponse: vriSchedulingReportResponseSchema,
    webPushPublicKeyResponse: webPushPublicKeyResponseSchema,
    waitingMediaResponse: waitingMediaResponseSchema,
    zoomConnectionsResponse: zoomConnectionsResponseSchema,
    zoomConnectUrlResponse: zoomConnectUrlResponseSchema,
    voicemailInboxResponse: voicemailInboxResponseSchema,
    voicemailUnreadCountResponse: voicemailUnreadCountResponseSchema
};
export const liveSmokeChecks = manifest.liveChecks;
export const adminSmokeChecks = manifest.adminChecks;
export const apiContractEntries = manifest.apiContracts;
function stripQuery(path) {
    return path.split('?')[0];
}
export function getApiResponseSchema(method, path) {
    const normalizedMethod = method.toUpperCase();
    const normalizedPath = stripQuery(path);
    const contract = apiContractEntries.find(entry => entry.method.toUpperCase() === normalizedMethod && stripQuery(entry.path) === normalizedPath);
    return contract ? apiContractSchemas[contract.schema] : null;
}
